import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Card, CardContent, Grid, TextField, Typography,
  Alert, IconButton, Chip, Tabs, Tab,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import GroupIcon from '@mui/icons-material/Group';
import { useSearchParams } from 'react-router-dom';
import { roleAPI } from '../services/api';
import { TENANT_PERMISSION_MODULES } from '../constants/permissions';
import { useAuth } from '../contexts/AuthContext';
import { PageHeader } from '../components/ui/PageHeader';
import { getPageSubtitle } from '../constants/pageSubtitles';
import { useSystemTotal } from '../hooks/useSystemTotal';
import { SuperAdminDataTable } from '../components/superadmin/SuperAdminDataTable';
import { UsersPanel } from '../components/users/UsersPanel';
import { StaffPanel } from '../components/staff/StaffPanel';
import { TeamsPanel } from '../components/roles/TeamsPanel';
import { PermissionMatrix, normalizePermissions, countViewModules } from '../components/roles/PermissionMatrix';

type PermMap = Record<string, Record<string, boolean>>;
type TabKey = 'users' | 'staff' | 'roles' | 'teams';

export const RolesPage: React.FC = () => {
  const { hasPermission } = useAuth();
  const { total: systemTotal, refresh: refreshTotal } = useSystemTotal('roles');
  const [searchParams, setSearchParams] = useSearchParams();
  const [roles, setRoles] = useState<any[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ roleName: '', description: '' });
  const [permissions, setPermissions] = useState<PermMap>(normalizePermissions());
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const canViewUsers = hasPermission('roles', 'view');
  const canViewStaff = hasPermission('staff', 'view');
  const canViewRoles = hasPermission('roles', 'view');
  const canViewTeams = hasPermission('teams', 'view');
  const canCreate = hasPermission('roles', 'create');
  const canEdit = hasPermission('roles', 'edit');
  const canDelete = hasPermission('roles', 'delete');

  const availableTabs = useMemo(() => {
    const tabs: TabKey[] = [];
    if (canViewUsers) tabs.push('users');
    if (canViewStaff) tabs.push('staff');
    if (canViewRoles) tabs.push('roles');
    if (canViewTeams) tabs.push('teams');
    return tabs;
  }, [canViewUsers, canViewStaff, canViewRoles, canViewTeams]);

  const tabParam = searchParams.get('tab') as TabKey | null;
  const tab: TabKey = tabParam && availableTabs.includes(tabParam) ? tabParam : (availableTabs[0] || 'users');

  useEffect(() => {
    if (tabParam !== tab) {
      setSearchParams({ tab }, { replace: true });
    }
  }, [tab, tabParam, setSearchParams]);

  const setTab = (next: TabKey) => setSearchParams({ tab: next }, { replace: true });

  const load = () => roleAPI.getAll().then((r) => {
    setRoles(r.data);
    refreshTotal();
  }).catch(() => setRoles([]));
  useEffect(() => { if (canViewRoles) load(); }, [canViewRoles]);

  const resetForm = () => {
    setEditingId(null);
    setForm({ roleName: '', description: '' });
    setPermissions(normalizePermissions());
  };

  const handleEdit = (role: any) => {
    setEditingId(role._id);
    setForm({ roleName: role.roleName, description: role.description || '' });
    setPermissions(normalizePermissions(role.permissions));
    setMessage('');
    setError('');
    setTab('roles');
  };

  const handleSave = async () => {
    if (!form.roleName.trim()) {
      setError('Role name is required');
      return;
    }
    try {
      const payload = { roleName: form.roleName.trim(), description: form.description, permissions };
      if (editingId) {
        if (!canEdit) return;
        await roleAPI.update(editingId, payload);
        setMessage('Role updated');
      } else {
        if (!canCreate) return;
        await roleAPI.create(payload);
        setMessage('Role created');
      }
      resetForm();
      load();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg || 'Failed to save role');
    }
  };

  const handleDelete = async (id: string, roleName: string, isDefault?: boolean) => {
    if (!canDelete || isDefault) return;
    if (!window.confirm(`Delete role "${roleName}"?`)) return;
    try {
      await roleAPI.remove(id);
      if (editingId === id) resetForm();
      setMessage('Role deleted');
      load();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg || 'Failed to delete role');
    }
  };

  const formTitle = useMemo(() => (editingId ? 'Edit Custom Role' : 'Create Custom Role'), [editingId]);

  return (
    <Box>
      <PageHeader
        title="Staff & Login"
        subtitle={getPageSubtitle('roles', 'Staff & Login', { total: systemTotal })}
        icon={<GroupIcon />}

      />

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v as TabKey)}
        sx={{
          minHeight: 36,
          mb: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
          '& .MuiTab-root': { minHeight: 36, py: 0.5, fontSize: 13, textTransform: 'none', fontWeight: 600 },
        }}
      >
        {canViewUsers && <Tab label="Login Users" value="users" />}
        {canViewStaff && <Tab label="Staff" value="staff" />}
        {canViewRoles && <Tab label="Custom Roles" value="roles" />}
        {canViewTeams && <Tab label="Teams" value="teams" />}
      </Tabs>

      {tab === 'users' && canViewUsers && <UsersPanel />}
      {tab === 'staff' && canViewStaff && <StaffPanel />}
      {tab === 'teams' && canViewTeams && <TeamsPanel />}

      {tab === 'roles' && canViewRoles && (
        <>
          {message && <Alert severity="success" sx={{ mb: 1 }} onClose={() => setMessage('')}>{message}</Alert>}
          {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError('')}>{error}</Alert>}

          <Grid container spacing={2}>
            <Grid item xs={12} md={7}>
              {(canCreate || canEdit) && (
                <Card>
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                      <Typography variant="subtitle1" fontWeight={700}>{formTitle}</Typography>
                      {editingId && (
                        <Button size="small" onClick={resetForm}>Cancel edit</Button>
                      )}
                    </Box>
                    <TextField
                      label="Role Name"
                      value={form.roleName}
                      onChange={(e) => setForm({ ...form, roleName: e.target.value })}
                      fullWidth
                      size="small"
                      sx={{ mb: 1.5 }}
                      disabled={!!editingId && !canEdit}
                    />
                    <TextField
                      label="Description"
                      value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      fullWidth
                      size="small"
                      sx={{ mb: 1.5 }}
                    />

                    <Typography fontWeight={700} fontSize={12} sx={{ mb: 1 }}>Module Permissions</Typography>
                    <PermissionMatrix permissions={permissions} onChange={setPermissions} />

                    <Button
                      variant="contained"
                      size="small"
                      sx={{ mt: 1.5 }}
                      onClick={handleSave}
                      disabled={editingId ? !canEdit : !canCreate}
                    >
                      {editingId ? 'Update Role' : 'Create Role'}
                    </Button>
                  </CardContent>
                </Card>
              )}
              {!canCreate && !canEdit && (
                <Alert severity="info">You have view-only access to roles.</Alert>
              )}
            </Grid>

            <Grid item xs={12} md={5}>
              <SuperAdminDataTable
                rows={roles}
                searchPlaceholder="Search roles..."
                recordLabel="roles"
                statusOptions={[{ label: 'All', value: 'all' }]}
                getStatus={() => 'active'}
                columns={[
                  {
                    id: 'name',
                    header: 'Role',
                    searchValue: (r) => `${r.roleName} ${r.description || ''}`,
                    render: (r) => (
                      <Box>
                        <Typography fontWeight={700} fontSize={13}>{r.roleName}</Typography>
                        <Typography fontSize={12} color="textSecondary">{r.description}</Typography>
                        {r.isDefault && <Chip label="System" size="small" sx={{ mt: 0.5 }} />}
                      </Box>
                    ),
                  },
                  {
                    id: 'modules',
                    header: 'Access',
                    render: (r) => (
                      <Typography fontSize={13}>
                        {countViewModules(normalizePermissions(r.permissions))} / {TENANT_PERMISSION_MODULES.length} modules
                      </Typography>
                    ),
                  },
                ]}
                renderActions={(r) => (
                  <Box>
                    {canEdit && (
                      <IconButton size="small" onClick={() => handleEdit(r)} aria-label="Edit role">
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                    {canDelete && !r.isDefault && (
                      <IconButton size="small" color="error" onClick={() => handleDelete(r._id, r.roleName, r.isDefault)} aria-label="Delete role">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                )}
              />
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
};
